import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { MapboxOverlay, MapboxOverlayProps } from '@deck.gl/mapbox';
import { Layer, log as deckLog } from '@deck.gl/core';
import { useControl, useMapInstance } from 'shared/map-exports';
import { ENV } from 'shared/ENV';
import { useDeckGlContextLossWorkaround } from './useDeckGlContextLossWorkaround';
import { registerDeckOverlay } from './deckOverlayPicking';
import { DECK_LAYER_DRAW_ORDER, DeckLayerGroup } from './DeckOverlay.constants';

// luma.gl's shader error viewer appends a full-screen overlay on shader compilation
// errors. in prod, disable it.
const DEBUG_SHADERS = ENV.PROD ? 'never' : 'errors';

// deck.gl defaults to level 0, which swallows its own warnings — including the
// one it logs when an icon key isn't in the atlas mapping, whose only other
// symptom is that nothing draws. Turn them on outside prod.
if (!ENV.PROD) {
  deckLog.level = 1;
}

/**
 * luma.gl keeps its own logger, separate from deck's, and persists the level in
 * sessionStorage. So a one-off `luma.log.level = 1` in the console — which
 * deck.gl's startup banner invites you to run — keeps tracing for the rest of
 * the session. At level 1 that includes a per-draw "setConstantAttributeWebGL(n)
 * could have been skipped" for every constant accessor, which the animated rotor
 * layer emits on every tick.
 *
 * Pin it back down. Warnings and errors log at level 0, so only the tracing
 * chatter goes away. Set at module scope, after the deck.gl imports above have
 * put `luma` on the global, so a dev who raises the level later still gets it.
 */
const lumaGlobal = (globalThis as { luma?: { log: { level: number } } }).luma;
if (lumaGlobal) {
  lumaGlobal.log.level = 0;
}

type DeckLayersByGroup = Partial<Record<DeckLayerGroup, Layer[]>>;

export type DeckOverlayContextValue = {
  registerLayers: (group: DeckLayerGroup, layers: Layer[]) => void;
};

export const DeckOverlayContext = createContext<DeckOverlayContextValue | null>(
  null,
);

const DeckGLOverlay = (props: MapboxOverlayProps): null => {
  const { onDeviceInitialized, loseWebGlContext } =
    useDeckGlContextLossWorkaround();

  // Read inside the callback rather than captured, so the probe always sees the
  // live overlay.
  const overlayRef = useRef<MapboxOverlay | null>(null);

  const overlay = useControl(
    () =>
      new MapboxOverlay({
        ...props,
        // deviceProps only applies at device creation, so it must be passed to the
        // constructor rather than setProps.
        deviceProps: { debugShaders: DEBUG_SHADERS },
        onDeviceInitialized,
      }),
  ) as MapboxOverlay;
  overlayRef.current = overlay;
  overlay.setProps(props);

  const mapboxMap = useMapInstance()?.getMap();
  useEffect(
    () => (mapboxMap ? registerDeckOverlay(mapboxMap, overlay) : undefined),
    [mapboxMap, overlay],
  );

  // Registered after the useControl call so this cleanup runs after
  // useControl's cleanup, which runs deck.finalize().
  useEffect(() => loseWebGlContext, [loseWebGlContext]);

  return null;
};

type DeckOverlayProps = {
  children?: ReactNode;
};

/**
 * Owns the map's single deck.gl overlay and lets descendants contribute layers
 * to it via `useDeckLayers`.
 *
 * There is deliberately one overlay per map rather than one per feature: each
 * `MapboxOverlay` creates its own Deck instance and WebGL context, and contexts
 * are both scarce and, on iOS, leaky (see `useDeckGlContextLossWorkaround`).
 *
 * `interleaved` is intentionally left off. Interleaving makes deck render
 * inside Mapbox's render loop, which forces a full map redraw on every deck
 * frame and costs significantly more CPU.
 */
export const DeckOverlay = (props: DeckOverlayProps): JSX.Element => {
  const { children } = props;
  const [layerGroups, setLayerGroups] = useState<DeckLayersByGroup>({});

  const registerLayers = useCallback(
    (group: DeckLayerGroup, layers: Layer[]): void => {
      setLayerGroups((current) =>
        current[group] === layers ? current : { ...current, [group]: layers },
      );
    },
    [],
  );

  const layers = useMemo(
    () => DECK_LAYER_DRAW_ORDER.flatMap((group) => layerGroups[group] ?? []),
    [layerGroups],
  );

  const handleDeckError = useCallback((error: Error, layer?: Layer): void => {
    if (!ENV.PROD) {
      console.error('deck.gl overlay error', layer?.id, error);
    }
    Sentry.captureException(error, {
      tags: { scope: 'deckGlOverlay', deckLayerId: layer?.id },
    });
  }, []);

  const contextValue = useMemo(() => ({ registerLayers }), [registerLayers]);

  return (
    <DeckOverlayContext.Provider value={contextValue}>
      <DeckGLOverlay layers={layers} onError={handleDeckError} />
      {children}
    </DeckOverlayContext.Provider>
  );
};
